import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '@/app/baseQuery';
import { sortByAccessLevel } from '@/scenes/Management/Community/membershipRolesUtil';
import Jsona from 'jsona';
import decamelizeKeysDeep from 'decamelize-keys-deep';

const jsona = new Jsona();

export interface CommunityMemberProfile {
  id: string;
  nickname?: string;
  settings?: unknown;
}

export interface CommunityMember {
  id: string;
  permissions: string[];
  profile: CommunityMemberProfile;
}

interface GetCommunityMembersParams {
  communityId: string;
}

interface UpdateCommunityParticipantParams {
  communityId: string;
  participantId: string;
  permissions: string[];
}

function serializeParticipant(participantId: string, permissions: string[]) {
  return decamelizeKeysDeep(
    jsona.serialize({
      stuff: { id: participantId, type: 'community-participant', permissions },
      includeNames: []
    }),
    '-'
  );
}

export const communitiesApi = createApi({
  reducerPath: 'communitiesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['CommunityMembers'],
  endpoints: builder => ({
    /**
     * Fetches a community's participants.
     *
     * GET /profiles/{communityId}/community-participants
     *
     * Side effect: sorts the response by access level (see sortByAccessLevel)
     * before it's cached.
     */
    getCommunityMembers: builder.query<CommunityMember[], GetCommunityMembersParams>({
      query: ({ communityId }) => `profiles/${communityId}/community-participants`,
      transformResponse: sortByAccessLevel,
      providesTags: ['CommunityMembers']
    }),

    /**
     * Updates a community participant's permissions.
     *
     * PATCH /community-participants/{participantId}
     *
     * Side effect: optimistically patches the cached member list in place
     * instead of invalidating it, so a permission change updates that member
     * without reshuffling the list; rolled back if the request fails.
     */
    updateCommunityParticipant: builder.mutation<CommunityMember, UpdateCommunityParticipantParams>({
      query: ({ participantId, permissions }) => ({
        url: `community-participants/${participantId}`,
        method: 'PATCH',
        body: serializeParticipant(participantId, permissions)
      }),
      async onQueryStarted({ communityId, participantId, permissions }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          communitiesApi.util.updateQueryData('getCommunityMembers', { communityId }, draft => {
            const member = draft.find(m => m.id === participantId);
            if (member) {
              member.permissions = permissions;
            }
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      }
    })
  })
});

export const { useGetCommunityMembersQuery, useUpdateCommunityParticipantMutation } = communitiesApi;
